import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { mindboxNotifyAddProductToList } from 'cityads-v3/utils/counterUtils';

import Alert from '@mui/material/Alert'
import Button, { ButtonProps } from '@mui/material/Button';
import FiranoIcon from 'cityads-v3/components/Icons/FiranoIcon';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Snackbar from '@mui/material/Snackbar'
import SplitButton from 'cityads-v3/components/Buttons/SplitButton';

import { connectToOffer } from 'cityads-v3/actions/offers';

import {
  USER_ROLE_CODE_WEBMASTER,
} from 'cityads-v3/reducers/auth';

import {
  SUBSCRIPTION_STATUS_ID_PENDING,
} from 'cityads-v3/reducers/offers';

import {
  useIsLinksToolEnabled,
  useIsFiranoToolEnabled,
  useOfferConnected,
  useOfferAccessRequested,
  useOfferData,
  useLoadWmOfferSubscription
} from 'cityads-v3/hooks/offers'

import { WebmasterVerificationStatusId } from 'cityads-v3/types/header'
import { RootState } from 'cityads-v3/types/reducer';

import config from 'cityads-v3/config';

type GetLinkButtonProps = {
  id: number,
  onClick?(): void,
  alreadyRequested?: JSX.Element,
  getActionLinkUrl?: string,
  buttonClasses?: {
    root: string,
    label: string
  },
  className?: string,
  view?: 'button' | 'icon',
  buttonProps?: ButtonProps<'a'>,
  iconButtonProps?: IconButtonProps<'a'>,
}

type ActionButton = {
  title: string,
  key: string,
  link?: string,
  onClick?: (evt: React.MouseEvent) => void,
  icon?: JSX.Element,
  disabled?: boolean,
}

export default function GetLinkButton({
  id,
  onClick,
  alreadyRequested,
  getActionLinkUrl,
  buttonClasses,
  className,
  view = 'button',
  buttonProps,
  iconButtonProps,
}: GetLinkButtonProps): JSX.Element {
  const { t } = useTranslation(['Offers', 'main']);
  const dispatch = useDispatch();
  const offer = useOfferData(id);
  const user = useSelector((state: RootState) => state.auth.user);
  const isOfferConnected = useOfferConnected(id);
  const isLinksToolEnabled = useIsLinksToolEnabled(id);
  const isOfferAccessRequested = useOfferAccessRequested(id);
  const isFiranoToolEnabled = useIsFiranoToolEnabled(id);
  const { search, pathname } = useLocation();

  useLoadWmOfferSubscription([id]);

  const handleClick = useCallback(() => {
    dispatch(mindboxNotifyAddProductToList('Online.GetLink', id));
    onClick?.();
  }, [dispatch, onClick, id])

  const verificationStatus = useSelector((state: RootState) => state.header.user?.verificationStatus ?? null);
  const [showVerificationWarning, setShowVerificationWarning] = useState<boolean>(false);
  const hideVerificationWarning = useCallback(() => {
    setShowVerificationWarning(false);
  }, [setShowVerificationWarning]);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [showConnectionConfirmation, setShowConnectionConfirmation] = useState<boolean>(false);
  const hideConnectionConfirmation = useCallback(() => {
    setShowConnectionConfirmation(false);
  }, [setShowConnectionConfirmation]);
  const [connectionError, setConnectionError] = useState<string>(null);
  const [showConnectionError, setShowConnectionError] = useState<boolean>(false);
  const hideConnectionError = useCallback(() => {
    setShowConnectionError(false);
  }, [setShowConnectionError]);

  const handleConnectClick = useCallback((evt: React.MouseEvent) => {
    evt.preventDefault();

    handleClick(); // notify all the trackers about the click

    if (verificationStatus !== WebmasterVerificationStatusId.Verified) {
      setShowVerificationWarning(true);
      return;
    }

    (async () => {
      setIsConnecting(true);
      try {
        await dispatch(connectToOffer(id));
        setShowConnectionConfirmation(true);
      } catch (err) {
        setConnectionError(err.message ?? t('main:forms.undefinedError'));
        setShowConnectionError(true);
      } finally {
        setIsConnecting(false);
      }
    })();
  }, [
    t,
    handleClick,
    verificationStatus,
    setShowVerificationWarning,
    setIsConnecting,
    dispatch,
    id,
    setShowConnectionConfirmation,
    setConnectionError,
    setShowConnectionError,
  ]);

  if (offer.subscriptionStatus === SUBSCRIPTION_STATUS_ID_PENDING) {
    return null;
  }

  const verificationWarningBar = (
    <Snackbar open={showVerificationWarning} autoHideDuration={config.SNACKBAR_AUTOHIDE_DURATION} onClose={hideVerificationWarning}>
      <Alert onClose={hideVerificationWarning} severity="error">
        <Trans t={t} i18nKey="notVerifiedYet">
          You are not verified yet. To use this offer you have to pass verification on your <Link to="/publisher/account">account page</Link>.
        </Trans>
      </Alert>
    </Snackbar>
  );

  const connectionConfirmationBar = (
    <Snackbar open={showConnectionConfirmation} autoHideDuration={config.SNACKBAR_AUTOHIDE_DURATION} onClose={hideConnectionConfirmation}>
      <Alert onClose={hideConnectionConfirmation} severity="success">
        <Trans t={t} i18nKey="Offers:requestDialog.saved">
          Your request has been submitted for review! You can check the status of your application on the
          &apos;<Link to="/publisher/requestsHistory">Request history</Link>&apos; page.
        </Trans>
      </Alert>
    </Snackbar>
  );

  const connectionErrorBar = (
    <Snackbar open={showConnectionError} autoHideDuration={config.SNACKBAR_AUTOHIDE_DURATION} onClose={hideConnectionError}>
      <Alert onClose={hideConnectionError} severity="error">
        {connectionError}
      </Alert>
    </Snackbar>
  );

  const getActionLink: ActionButton = {
    title: t('exports.getActionLink'),
    key: 'getActionLink',
    link: getActionLinkUrl ?? `${pathname}/${id}/getActionLink${search}`,
    icon: <LinkOutlinedIcon />
  };

  const requestLink: ActionButton = {
    title: t('exports.connect'),
    key: 'connect',
    onClick: handleConnectClick,
    icon: <LockOutlinedIcon />,
    disabled: isConnecting,
  };

  const registerLink: ActionButton = {
    title: t('main:footer.startWork'),
    key: 'register',
    link: `/publisher/offer/${id}/register/publisher`
  };

  const firanoLink: ActionButton = {
    title: t('exports.getFiranoLink'),
    key: 'getFiranoLink',
    link: `/publisher/firano?offerId=${offer.id}`,
    icon: <FiranoIcon />
  }

  const buttons: ActionButton[] = [];
  if (user?.user_role_code === USER_ROLE_CODE_WEBMASTER) {
    if (isOfferConnected) {
      if (isFiranoToolEnabled) {
        buttons.push(firanoLink);
      }

      if (isLinksToolEnabled) {
        buttons.push(getActionLink);
      }
    } else if (!isOfferAccessRequested && !user?.isBanned) {
      buttons.push(requestLink);
    }
  } else {
    buttons.push(registerLink);
  }

  if (view === 'icon') {
    return (
      <>
        {verificationWarningBar}
        {connectionConfirmationBar}
        {connectionErrorBar}
        {buttons.filter(button => button.icon).map(button => (
          <IconButton
            {...iconButtonProps}
            key={button.key}
            component={button.link ? Link : undefined}
            to={button.link}
            onClick={button.onClick ?? handleClick}
            disabled={button.disabled}
            size="large"
          >
            {button.icon}
          </IconButton>
        ))}
      </>
    );
  }

  return (
    <>
      {verificationWarningBar}
      {connectionConfirmationBar}
      {connectionErrorBar}
      {buttons?.length ? (
        <SplitButton
          menuOptions={buttons.slice(1)}
          className={className}
        >
          <Button
            fullWidth={true}
            color="primary"
            component={buttons[0].link ? Link : undefined}
            to={buttons[0].link}
            variant="contained"
            classes={{
              ...buttonClasses,
            }}
            onClick={buttons[0].onClick ?? handleClick}
            disabled={buttons[0].disabled}
            {...buttonProps}
          >
            {buttons[0].title}
          </Button>
        </SplitButton>
      ) : user?.isBanned ? null : alreadyRequested ?? null}
    </>
  );
}

GetLinkButton.propTypes = {
  id: PropTypes.number,
  alreadyRequested: PropTypes.element,
  onClick: PropTypes.func,
}
